import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/shared/components/ui/button';
import {
  Download,
  BookOpen,
  X,
  MessageSquare,
  Loader2,
  Phone,
  Clock,
  Heart,
  Home,
} from 'lucide-react';
import { apiClient } from '@/core/api/client';
import { Textarea } from '@/shared/components/ui/textarea';
import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useGuestSignupModal } from '@/features/pre-login/context/GuestSignupModalContext';
import { CarePlanSkeleton } from '@/features/resource-hub/components/CarePlanSkeleton';
import {AiResourceSkeleton} from "@/features/resource-hub/components/AiResourceSkeleton";
import { downloadCarePlanPdf } from '@/features/chatbot/api/carePlanPdfApi';
import { CareTeamMessageGate } from './CareTeamMessageGate';

type CarePlanState = {
  sessionId?: string;
};

type CareTeamProvider = {
  id: string;
  name: string;
  specialty: string;
  category: string;
  bio: string;
  rank: number;
};

type ApiCategory = {
  category_name: string;
};

type ApiProvider = {
  provider_id: string;
  company_name: string;
  provider_bio: string;
  rating: number | null;
  categories: ApiCategory[];
};

type ApiSpecialty = {
  specialty_name: string;
  specialty_type: 'PRIMARY' | 'SECONDARY';
  rank_order: number;
  providers: ApiProvider[];
};

type SelectedProvidersResponse = {
  care_plan_name: string;
  specialties: ApiSpecialty[];
};

type SuggestedResource = {
  id: string;
  title: string;
  seoUrl: string;
  category: string;
  type: string;
};

type SuggestedResourcesApiResponse = {
  success: boolean;
  resources: SuggestedResource[];
};

const getInitials = (name: string) =>
  name
    .split(' ')
    .slice(0, 3)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();

export function CarePlan() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { openModal: openGuestSignupModal } = useGuestSignupModal();
 
  const locationState = location.state as CarePlanState | null;
  // Resolve sessionId: location.state → sessionStorage → localStorage (survives refresh)
  const rawSessionId =
    locationState?.sessionId ||
    sessionStorage.getItem('bquest_nav_session_id') ||
    localStorage.getItem('bquest_care_plan_session_id') ||
    undefined;
  const sessionId = rawSessionId || undefined;

  // Persist sessionId to localStorage so the page survives a full refresh
  useEffect(() => {
    if (sessionId) {
      localStorage.setItem('bquest_care_plan_session_id', sessionId);
    }
  }, [sessionId]);

  const [conciergeOpen, setConciergeOpen] = useState(false);

  useEffect(() => {
    if (!conciergeOpen) return;
    window.history.pushState({ concierge: true }, '');
    const handlePop = () => setConciergeOpen(false);
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [conciergeOpen]);

  


  const [consultationModalOpen, setConsultationModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<CareTeamProvider | null>(null);
  const [consultationMessage, setConsultationMessage] = useState('');
  const [isGeneratingMessage, setIsGeneratingMessage] = useState(false);
  const pollIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearTimeout(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  useEffect(() => stopPolling, []);
  const [preferredTimes, setPreferredTimes] = useState<string[]>([]);
  const [preferredDays, setPreferredDays] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
 const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [primaryExpanded, setPrimaryExpanded] = useState(false);
  const [expandedBios, setExpandedBios] = useState<Set<string>>(new Set());
  const [visibleProvidersMap, setVisibleProvidersMap] = useState<Record<string, number>>({});
  const primaryCardRef = useRef<HTMLDivElement>(null);

  const handleContactPrimary = () => {
    primaryCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const handleDownloadCarePlan = async () => {
    if (!sessionId || isDownloadingPdf) return;
    setIsDownloadingPdf(true);
    try {
      await downloadCarePlanPdf(sessionId);
    } catch (err) {
      console.error('Failed to download care plan PDF', err);
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const { data: carePlanData } = useQuery<SelectedProvidersResponse>({
    queryKey: ['selected-providers-plan', sessionId],
    enabled: !!sessionId,
    staleTime: Infinity,
    gcTime: Infinity,
    queryFn: async () => {
      const { data } = await apiClient.get<SelectedProvidersResponse>(
        '/find-providers/selected-providers',
        { params: { session_id: sessionId } }
      );
      return data;
    },
  });
  

  const {
  data: suggestedResourcesData,
  isLoading: loading,
} = useQuery<SuggestedResourcesApiResponse>({
  queryKey: ['suggested-resources', sessionId],
  enabled: !!sessionId,
  staleTime: Infinity,
  gcTime: Infinity,
  queryFn: async () => {
    const { data } = await apiClient.get<SuggestedResourcesApiResponse>(
      '/resources/suggested-resources'
    );
    return data;
  },
});

  const suggestedResources = suggestedResourcesData?.resources ?? [];
  const specialties = carePlanData?.specialties ?? [];
  const carePlanName = carePlanData?.care_plan_name ?? '';
  const primarySpecialty = specialties.find((s) => s.specialty_type === 'PRIMARY');
  const secondarySpecialties = specialties.filter((s) => s.specialty_type === 'SECONDARY');
  const primaryProviders = primarySpecialty?.providers ?? [];
  const primaryProvider = primaryProviders[0];
  const primaryVisibleCount = visibleProvidersMap[primarySpecialty?.specialty_name ?? ''] ?? 1;

  const getVisibleCount = (specialtyName: string) => visibleProvidersMap[specialtyName] ?? 1;

  const showMoreProvider = (specialtyName: string) => {
    setVisibleProvidersMap((prev) => ({
      ...prev,
      [specialtyName]: (prev[specialtyName] ?? 1) + 1,
    }));
  };

  const toggleBio = (providerId: string) => {
    setExpandedBios((prev) => {
      const next = new Set(prev);
      next.has(providerId) ? next.delete(providerId) : next.add(providerId);
      return next;
    });
  };

  type GenerateStatusResponse =
    | { status: 'READY'; schedule_message: { message: string } }
    | { status: 'PENDING' }
    | { status: 'ERROR' };

  const POLL_INTERVAL_MS = 1500;
  const MAX_POLL_ATTEMPTS = 20; // ~30s ceiling before falling back to a blank greeting

  const openConsultationModal = async (provider: CareTeamProvider) => {
    stopPolling();
    // Booking a consultation creates an owned record + contacts the provider — it
    // needs a real account. Guests are routed to the signup modal instead.
    if (!isAuthenticated) {
      openGuestSignupModal({ trigger: 'action_button_click', triggerEntityId: provider.id });
      return;
    }
    setSelectedProvider(provider);
    setPreferredTimes([]);
    setPreferredDays([]);
    setConsultationMessage(`Hello ${provider.name},\n\n`);
    setSubmitError('');
    setSubmitSuccess(false);
    setConsultationModalOpen(true);
    setIsGeneratingMessage(true);

    const applyMessage = (apiMessage: string) => {
      setConsultationMessage(`Hello ${provider.name},\n\n${apiMessage}`);
      setIsGeneratingMessage(false);
    };

    try {
      const { data } = await apiClient.post<GenerateStatusResponse>(
        '/provider-messages/generate',
        { session_id: sessionId },
      );

      if (data.status === 'READY') {
        applyMessage(data.schedule_message?.message ?? '');
        return;
      }

      // PENDING — poll until READY, ERROR, or timeout. Uses a setTimeout chain
      // (not setInterval) so the next poll is only scheduled after the current
      // request settles — avoids overlapping in-flight requests if the API is
      // slower than POLL_INTERVAL_MS.
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const { data: statusData } = await apiClient.get<GenerateStatusResponse>(
            '/provider-messages/generate-status',
            { params: { session_id: sessionId } },
          );

          if (statusData.status === 'READY') {
            stopPolling();
            applyMessage(statusData.schedule_message?.message ?? '');
            return;
          }
          if (statusData.status === 'ERROR' || attempts >= MAX_POLL_ATTEMPTS) {
            stopPolling();
            setIsGeneratingMessage(false);
            return;
          }
          pollIntervalRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        } catch {
          stopPolling();
          setIsGeneratingMessage(false);
        }
      };
      pollIntervalRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    } catch {
      setIsGeneratingMessage(false);
    }
  };

  const handleSendConsultationRequest = async () => {
    if (!selectedProvider) return;

    if (!consultationMessage.trim()) {
      setSubmitError('Please describe what you need');
      return;
    }
    if (preferredTimes.length === 0) {
      setSubmitError('Please select at least one time of day');
      return;
    }
    if (preferredDays.length === 0) {
      setSubmitError('Please select at least one day of the week');
      return;
    }

    setSubmitError('');
    setIsSubmitting(true);
    try {
      await apiClient.post('/consultations', {
        providerId: selectedProvider.id,
        description: consultationMessage.trim(),
        availabilityTimes: preferredTimes.map((t) => t.toUpperCase()),
        availabilityDays: preferredDays.map((d) => d.toUpperCase()),
      });
      setSubmitSuccess(true);
      setTimeout(() => {
        setConsultationModalOpen(false);
        setSubmitSuccess(false);
      }, 1500);
    } catch (err: unknown) {
      const errorMessage =
        (err as { message?: string }).message || 'Failed to send request. Please try again.';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleTime = (time: string) => {
    setPreferredTimes((prev) =>
      prev.includes(time) ? prev.filter((t) => t !== time) : [...prev, time]
    );
  };

  const toggleDay = (day: string) => {
    setPreferredDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };
  const handleViewProfile = (providerId: string) => {
    navigate(`/providers/${providerId}?discoverySource=NAVIGATOR`, {
      state: { from: '/detailed-care-navigator/plan' },
    });
  };

  const isLoadingPlan = !!sessionId && !carePlanData;

  if (isLoadingPlan) {
    return (
      <div className="min-h-screen">
        <CarePlanSkeleton />
      </div>
    );
  }

  if (conciergeOpen) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        {/* <JourneyProgress currentStep="plan" /> */}
        <div className="flex-1 flex items-start justify-center px-4 py-12">
          <div className="bg-white rounded-2xl shadow-md w-full max-w-xl p-8">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                <Heart size={28} className="text-[#3d7872]" />
              </div>
            </div>

            <h1 className="text-2xl font-bold text-gray-900 text-center mb-6">
              We'd Like to Help Personally
            </h1>

            <p className="text-[#3d7872] leading-relaxed mb-5">
              Your situation requires the thoughtful attention of one of our Care Concierge
              specialists. They have deep expertise in navigating complex care needs and will
              work with you one-on-one to create the right plan.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              <span className="font-bold">Everything you've shared has been saved.</span>{' '}
              You won't need to repeat yourself. A member of our team will reach out within
              one business day to continue your care planning journey.
            </p>

            <div className="bg-blue-50 rounded-xl p-4 mb-8 flex gap-3">
              <Clock size={20} className="text-[#3d7872] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-gray-900 text-sm mb-1">Expected Response Time</p>
                <p className="text-sm text-gray-600">
                  A Care Concierge will contact you by email or phone within 24 hours (on business
                  days)
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <Button
                className="w-full h-12 bg-[#3d7872] hover:bg-[#2d6862] text-white text-base font-semibold rounded-xl gap-2"
                onClick={() => navigate('/dashboard')}
              >
                <Home size={18} />
                Return to Dashboard
              </Button>
              <Button
                variant="outline"
                className="w-full h-12 border-[#3d7872] text-[#3d7872] hover:bg-[#3d7872] hover:text-white text-base font-semibold rounded-xl"
                onClick={() => navigate('/resources')}
              >
                Browse Resources
              </Button>
            </div>

            <p className="text-center text-sm text-gray-500">
              Need immediate assistance? Call our 24/7 helpline:{' '}
              <a href="tel:8005553273" className="text-[#3d7872] font-semibold hover:underline">
                (800) 555-CARE
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* <JourneyProgress currentStep="plan" /> */}

      <div className="bg-white">
        <div className=" px-6 py-8">
          <div className="container mx-auto">
            <h1 className="text-3xl font-bold text-[#3d7872] mb-1">
              Let's address your care needs
            </h1>
            <p className="text-gray-500 text-sm mb-4">{carePlanName}</p>
            <hr className="border-gray-200 mb-6" />

            <div className="relative bg-white rounded-2xl shadow-md overflow-hidden mb-6 w-full border-l-4 border-[#3d7872]">
              <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full bg-slate-100 opacity-70 pointer-events-none" />
              <div className="absolute -bottom-8 -left-6 w-28 h-28 rounded-full bg-slate-100 opacity-70 pointer-events-none" />
              <div className="relative pl-12 pr-8 py-16">
                <p className="text-[#3d6b72] leading-relaxed mb-4">
                  Every aspect of <strong>your care is important</strong>. Based on your specific
                  situation, bQuest has curated a list of certified Service Providers and educational
                  resources just for you.
                </p>
                <p className="text-[#3d6b72] leading-relaxed">
                  We've selected a <strong>Primary Specialist</strong> that we suggest connecting with
                  first. Feel free to connect with them or any of the other providers below when you are
                  ready, or just learn more about what they do.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Button
                className="bg-[#3d7872] hover:bg-[#2d6862] text-white gap-2"
                onClick={handleContactPrimary}
              >
                <Phone size={16} />
                Contact Primary Specialist
              </Button>
              <Button
                variant="outline"
                className="border-[#3d7872] text-[#3d7872] hover:bg-[#3d7872] hover:text-white gap-2"
                onClick={handleDownloadCarePlan}
                disabled={!sessionId || isDownloadingPdf}
              >
                {isDownloadingPdf ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                Download Care Plan
              </Button>
              {/* <Button
                variant="outline"
                className="border-[#3d7872] text-[#3d7872] hover:bg-[#3d7872] hover:text-white"
                onClick={() => setConciergeOpen(true)}
              >
                Request Concierge Support
              </Button> */}
            </div>

            <span className="inline-flex items-center bg-orange-500 text-white text-sm  px-5 py-2 rounded-xl">
              Primary Need
            </span>

            {/* Specialist Cards */}
            <div className="mt-4">

              {/* Primary Card — full width */}
              {primaryProviders.length > 0 && <div ref={primaryCardRef}>
              {!primaryExpanded ? (
                <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm mb-4">
                  <div className="bg-[#3d7872] text-white px-5 py-3 flex items-center justify-between">
                    <span className="font-semibold text-sm">
                      {primarySpecialty?.specialty_name ?? 'Primary Specialist'}
                    </span>
                    <button
                      onClick={() => setPrimaryExpanded(true)}
                      className="text-white text-xs font-medium hover:text-white/80 transition-colors"
                    >
                      Learn More
                    </button>
                  </div>
                  {primaryProvider && (
                    <div className="bg-white p-5">
                      <div className="flex gap-4">
                        <div className="w-14 h-14 rounded-full bg-[#3d7872] flex items-center justify-center text-white font-bold text-base flex-shrink-0">
                          {getInitials(primaryProvider.company_name)}
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-gray-900 text-base">{primaryProvider.company_name}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <p className="text-sm text-gray-500">{primaryProvider.categories[0]?.category_name}</p>
                            {primaryProvider.rating != null && (
                              <div className="flex items-center gap-1">
                                <span className="text-amber-400 text-sm">★</span>
                                <span className="text-sm font-semibold text-gray-800">{primaryProvider.rating.toFixed(1)}</span>
                              </div>
                            )}
                          </div>
                          <p className={`text-sm text-gray-600 mt-2 leading-relaxed ${expandedBios.has(primaryProvider.provider_id) ? '' : 'line-clamp-3'}`}>
                            {primaryProvider.provider_bio}
                          </p>
                          <button
                            className="mt-1 text-xs font-medium text-[#3d7872] hover:underline"
                            onClick={() => toggleBio(primaryProvider.provider_id)}
                          >
                            {expandedBios.has(primaryProvider.provider_id) ? 'Read Less' : 'Read More'}
                          </button>
                          <div className="flex gap-3 mt-4 flex-wrap">
                            <CareTeamMessageGate
                              providerId={primaryProvider.provider_id}
                              providerName={primaryProvider.company_name}
                              sourceSurface="navigator"
                              isGuest={!isAuthenticated}
                              onGuestGate={() =>
                                openGuestSignupModal({
                                  trigger: 'action_button_click',
                                  triggerEntityId: primaryProvider.provider_id,
                                })
                              }
                            >
                              {(onMessage) => (
                                <button
                                  onClick={onMessage}
                                  className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                  <MessageSquare size={14} />
                                  Message
                                </button>
                              )}
                            </CareTeamMessageGate>
                            <button
                              onClick={() => openConsultationModal({
                                id: primaryProvider.provider_id,
                                name: primaryProvider.company_name,
                                specialty: primaryProvider.categories[0]?.category_name ?? '',
                                category: primarySpecialty?.specialty_name ?? '',
                                bio: primaryProvider.provider_bio,
                                rank: 0,
                              })}
                              className="flex items-center gap-1.5 px-4 py-2 bg-[#3d7872] text-white rounded-full text-sm font-medium hover:bg-[#2d6862] transition-colors"
                            >
                              <Phone size={14} />
                              Schedule Call
                            </button>
                            <button
                              onClick={() => handleViewProfile(primaryProvider.provider_id)}
                              className="px-4 py-2 border border-gray-300 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              View Profile
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm mb-4">
                  <div className="bg-[#3d7872] text-white px-5 py-3 flex items-center justify-between">
                    <span className="font-semibold text-sm">
                      {primarySpecialty?.specialty_name ?? 'Primary Specialist'}
                    </span>
                    <button
                      onClick={() => setPrimaryExpanded(false)}
                      className="text-white text-xs font-medium hover:text-white/80 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                  <div className="bg-white p-6">
                    <div className="">
                      {/* Left: provider info */}
                      <div className="lg:col-span-2">
                        {primaryProviders.slice(0, primaryVisibleCount).map((provider) => (
                          <div key={provider.provider_id}>
                            <p className={`text-[#3d7872] text-sm leading-relaxed mb-3 ${expandedBios.has(provider.provider_id) ? '' : 'line-clamp-3'}`}>
                              {provider.provider_bio}
                            </p>
                            <button
                              className="text-xs font-medium text-[#3d7872] hover:underline mb-3 block"
                              onClick={() => toggleBio(provider.provider_id)}
                            >
                              {expandedBios.has(provider.provider_id) ? 'Read Less' : 'Read More'}
                            </button>
                            <a
                              href="#"
                              onClick={(e) => e.preventDefault()}
                              className="text-[#3d7872] text-sm font-medium hover:underline"
                            >
                              Learn more about this type of specialist →
                            </a>
                            <hr className="my-5 border-gray-200" />
                            <div className="flex items-center gap-2 mb-4">
                              <span className="text-xs font-semibold text-[#3d7872] uppercase tracking-wide">Matched Provider</span>
                              <span className="bg-[#3d7872] text-white text-xs px-2.5 py-1 rounded-full font-semibold">Top Match</span>
                            </div>
                            <div className="border border-gray-200 rounded-xl p-4 mb-4">
                              <div className="flex gap-4 mb-3">
                                <div className="w-14 h-14 rounded-full bg-[#3d7872] flex items-center justify-center text-white font-bold text-base flex-shrink-0">
                                  {getInitials(provider.company_name)}
                                </div>
                                <div>
                                  <p className="font-bold text-gray-900 text-base">{provider.company_name}</p>
                                  <div className="flex items-center gap-3 mt-0.5">
                                    <p className="text-sm text-[#3d7872]">{provider.categories[0]?.category_name}</p>
                                    {provider.rating != null && (
                                      <div className="flex items-center gap-1">
                                        <span className="text-amber-400 text-sm">★</span>
                                        <span className="text-sm font-semibold text-gray-800">{provider.rating.toFixed(1)}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-3 flex-wrap">
                                <CareTeamMessageGate
                                  providerId={provider.provider_id}
                                  providerName={provider.company_name}
                                  sourceSurface="navigator"
                                  isGuest={!isAuthenticated}
                                  onGuestGate={() =>
                                    openGuestSignupModal({
                                      trigger: 'action_button_click',
                                      triggerEntityId: provider.provider_id,
                                    })
                                  }
                                >
                                  {(onMessage) => (
                                    <button
                                      onClick={onMessage}
                                      className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                      <MessageSquare size={14} />
                                      Message
                                    </button>
                                  )}
                                </CareTeamMessageGate>
                                <button
                                  onClick={() => openConsultationModal({
                                    id: provider.provider_id,
                                    name: provider.company_name,
                                    specialty: provider.categories[0]?.category_name ?? '',
                                    category: primarySpecialty?.specialty_name ?? '',
                                    bio: provider.provider_bio,
                                    rank: 0,
                                  })}
                                  className="flex items-center gap-1.5 px-4 py-2 bg-[#3d7872] text-white rounded-lg text-sm font-medium hover:bg-[#2d6862] transition-colors"
                                >
                                  <Phone size={14} />
                                  Schedule Call
                                </button>
                                <button
                                  onClick={() => handleViewProfile(provider.provider_id)}
                                  className="px-4 py-2 border border-gray-300 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                  View Profile
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                        {primaryVisibleCount < primaryProviders.length && (
                          <button
                            className="text-[#3d7872] text-sm font-medium hover:underline block mb-6"
                            onClick={() => showMoreProvider(primarySpecialty?.specialty_name ?? '')}
                          >
                            View 1 More Provider →
                          </button>
                        )}
                      </div>

                      {/* Right: Key Resources */}
                      {/* <div className="lg:col-span-1">
                        <p className="text-[#3d7872] font-semibold text-sm mb-3">Key Resources</p>
                        <div className="border border-gray-200 rounded-xl p-4 mb-3">
                          <p className="text-xs text-[#3d7872] font-medium mb-1">bQuest Guide</p>
                          <p className="font-bold text-gray-900 text-sm mb-1">
                            {primaryProvider?.categories[0]?.category_name}
                          </p>
                          <p className="text-xs text-gray-600 leading-relaxed mb-2">
                            A comprehensive guide to diagnosis, treatment options, and caregiver support
                          </p>
                          <a href="#" className="text-[#3d7872] text-xs font-medium hover:underline">Read guide →</a>
                        </div>
                        <div className="border border-gray-200 rounded-xl p-4">
                          <p className="text-xs text-[#3d7872] font-medium mb-2">Other resources</p>
                          <div className="flex items-start gap-2">
                            <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <BookOpen size={14} className="text-gray-500" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-900">
                                {primaryProvider?.categories[0]?.category_name}
                              </p>
                              <a href="#" className="text-[#3d7872] text-xs font-medium hover:underline">Read guide →</a>
                            </div>
                          </div>
                        </div>
                      </div> */}
                    </div>
                  </div>
                </div>
              )}
              </div>}

              {/* Secondary Specialist Cards — each expands in-place */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                {secondarySpecialties.map((specialty, idx) => {
                  const visibleCount = getVisibleCount(specialty.specialty_name);
                  const firstProvider = specialty.providers[0];
                  return (
                    <div key={specialty.specialty_name} className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
                      <div className="bg-[#3d7872] text-white px-5 py-3 flex items-center justify-between">
                        <span className="font-semibold text-sm">{specialty.specialty_name}</span>
                        <button
                          onClick={() =>
                                setExpandedCard(
                                  expandedCard === specialty.specialty_name
                                    ? null
                                    : specialty.specialty_name
                                )
                              }
                          className="text-white text-xs font-medium hover:text-white/80 transition-colors"
                        >
                          {expandedCard === specialty.specialty_name   ? 'Close' : 'Learn More'}
                        </button>
                      </div>

                      {expandedCard === specialty.specialty_name ? (
                        <div className="bg-white p-5">
                          {specialty.providers.slice(0, visibleCount).map((provider) => (
                            <div key={provider.provider_id} className="mb-2">
                              <p className={`text-[#3d7872] text-sm leading-relaxed mb-2 ${expandedBios.has(provider.provider_id) ? '' : 'line-clamp-3'}`}>
                                {provider.provider_bio}
                              </p>
                              <button
                                className="text-xs font-medium text-[#3d7872] hover:underline mb-2 block"
                                onClick={() => toggleBio(provider.provider_id)}
                              >
                                {expandedBios.has(provider.provider_id) ? 'Read Less' : 'Read More'}
                              </button>
                              <a href="#" className="text-[#3d7872] text-sm font-medium hover:underline">
                                Learn more about this type of specialist →
                              </a>
                              <hr className="my-4 border-gray-200" />
                              <div className="flex items-center gap-2 mb-3">
                                <span className="text-xs font-semibold text-[#3d7872] uppercase tracking-wide">Matched Provider</span>
                                <span className="bg-[#3d7872] text-white text-xs px-2 py-0.5 rounded-full font-semibold">Top Match</span>
                              </div>
                              <div className="border border-gray-200 rounded-xl p-4 mb-4">
                                <div className="flex gap-3 mb-3">
                                  <div className="w-10 h-10 rounded-full bg-[#3d7872] flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                                    {getInitials(provider.company_name)}
                                  </div>
                                  <div>
                                    <p className="font-semibold text-gray-900 text-sm">{provider.company_name}</p>
                                    <div className="flex items-center gap-3 mt-0.5">
                                      <p className="text-xs text-[#3d7872]">{provider.categories[0]?.category_name}</p>
                                      {provider.rating != null && (
                                        <div className="flex items-center gap-1">
                                          <span className="text-amber-400 text-xs">★</span>
                                          <span className="text-xs font-semibold text-gray-800">{provider.rating.toFixed(1)}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                  <CareTeamMessageGate
                                    providerId={provider.provider_id}
                                    providerName={provider.company_name}
                                    sourceSurface="navigator"
                                    isGuest={!isAuthenticated}
                                    onGuestGate={() =>
                                      openGuestSignupModal({
                                        trigger: 'action_button_click',
                                        triggerEntityId: provider.provider_id,
                                      })
                                    }
                                  >
                                    {(onMessage) => (
                                      <button
                                        onClick={onMessage}
                                        className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-full text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                      >
                                        <MessageSquare size={12} />
                                        Message
                                      </button>
                                    )}
                                  </CareTeamMessageGate>
                                  <button
                                    onClick={() => openConsultationModal({
                                      id: provider.provider_id,
                                      name: provider.company_name,
                                      specialty: provider.categories[0]?.category_name ?? '',
                                      category: specialty.specialty_name,
                                      bio: provider.provider_bio,
                                      rank: idx + 1,
                                    })}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#3d7872] text-white rounded-full text-xs font-medium hover:bg-[#2d6862] transition-colors"
                                  >
                                    <Phone size={12} />
                                    Schedule Call
                                  </button>
                                  <button
                                      onClick={() => handleViewProfile(provider.provider_id)}
                                      className="px-4 py-2 border border-gray-300 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                      View Profile
                                    </button>
                                </div>
                              </div>
                            </div>
                          ))}
                          {visibleCount < specialty.providers.length && (
                            <button
                              className="text-[#3d7872] text-sm font-medium hover:underline block mb-4"
                              onClick={() => showMoreProvider(specialty.specialty_name)}
                            >
                              View 1 More Provider →
                            </button>
                          )}
                          
                          {/* <div className="border border-gray-200 rounded-xl p-3 mb-2">
                            <p className="text-xs text-[#3d7872] font-medium mb-1">bQuest Guide</p>
                            <p className="font-bold text-gray-900 text-xs mb-1">
                              {firstProvider?.categories[0]?.category_name}
                            </p>
                            <p className="text-xs text-[#3d7872] mb-1">
                              Practical modifications and safety measures for dementia care at home
                            </p>
                            <a href="#" className="text-[#3d7872] text-xs font-medium hover:underline">Read guide →</a>
                          </div> */}
                          {/* <div className="border border-gray-200 rounded-xl p-3">
                            <p className="text-xs text-[#3d7872] font-medium mb-2">Other resources</p>
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                                <BookOpen size={12} className="text-gray-500" />
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-gray-900">
                                  {firstProvider?.categories[0]?.category_name}
                                </p>
                                <a href="#" className="text-[#3d7872] text-xs font-medium hover:underline">Read guide →</a>
                              </div>
                            </div>
                          </div> */}
                        </div>
                      ) : (
                        <div className="bg-white p-5">
                          {firstProvider && (
                            <>
                              <div className="flex gap-3 mb-3">
                                <div className="w-10 h-10 rounded-full bg-[#3d7872] flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                                  {getInitials(firstProvider.company_name)}
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-900 text-sm">{firstProvider.company_name}</p>
                                  <div className="flex items-center gap-3 mt-0.5">
                                    <p className="text-xs text-gray-500">{firstProvider.categories[0]?.category_name}</p>
                                    {firstProvider.rating != null && (
                                      <div className="flex items-center gap-1">
                                        <span className="text-amber-400 text-xs">★</span>
                                        <span className="text-xs font-semibold text-gray-800">{firstProvider.rating.toFixed(1)}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <p className={`text-sm text-[#3d7872] leading-relaxed ${expandedBios.has(firstProvider.provider_id) ? '' : 'line-clamp-3'}`}>
                                {firstProvider.provider_bio}
                              </p>
                              <button
                                className="mt-1 text-xs font-medium text-[#3d7872] hover:underline"
                                onClick={() => toggleBio(firstProvider.provider_id)}
                              >
                                {expandedBios.has(firstProvider.provider_id) ? 'Read Less' : 'Read More'}
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Resources about your care stage */}
            
                <div className="mt-10">
                  <h2 className="text-2xl font-bold text-[#3d7872] mb-1">
                    Resources about your care stage
                  </h2>
                  <p className="text-gray-500 text-sm mb-6">
                    Educational materials to help you understand and navigate your care journey
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {loading
                  ? Array.from({ length: 6 }).map((_, index) => (
                      <AiResourceSkeleton key={index} />
                    ))
                  : suggestedResources.map((resource) => (
                      <Link
                        key={resource.id}
                        to={`/resource-hub-detail/${resource.seoUrl}`}
                        className="flex flex-col bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow no-underline"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <BookOpen size={18} className="text-gray-500" />
                          </div>

                          <span className="text-xs font-medium text-gray-600 border border-gray-200 rounded-full px-2.5 py-0.5">
                            {resource.type || resource.category || "Resource"}
                          </span>
                        </div>

                        <p className="font-bold text-gray-900 text-sm mb-1 flex-1">
                          {resource.title}
                        </p>

                        {resource.category && (
                          <p className="text-xs text-gray-500 mb-3">
                            {resource.category}
                          </p>
                        )}

                        <span className="text-[#3d7872] text-xs font-medium hover:underline mt-auto">
                          Access resource →
                        </span>
                      </Link>
                    ))}
              </div>
                </div>
              
            </div>
          </div>
        </div>
      </div>

      {/* Consultation Modal */}
      {consultationModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#3d7872] rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-semibold text-white mb-2">Request Consultation</h2>
                  <p className="text-white/80 text-sm">
                    Submit a consultation request to {selectedProvider?.name}. They will review your
                    request and respond directly.
                  </p>
                </div>
                <button
                  onClick={() => {
                    stopPolling();
                    setConsultationModalOpen(false);
                  }}
                  className="text-white/80 hover:text-white transition-colors ml-4 flex-shrink-0"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-white font-medium mb-2">
                    How can we help? <span className="text-red-300">*</span>
                  </label>
                  <div className="relative">
                    <Textarea
                      placeholder="Describe your care needs, questions, or what you're looking for..."
                      value={consultationMessage}
                      onChange={(e) => setConsultationMessage(e.target.value)}
                      className="w-full resize-none bg-white text-gray-900 border-none rounded-md p-3"
                      rows={8}
                      maxLength={2000}
                    />
                    {isGeneratingMessage && (
                      <div className="absolute bottom-2 right-2 flex items-center gap-1.5 rounded bg-white/90 px-2 py-1 text-xs text-gray-600">
                        <Loader2 size={12} className="animate-spin" />
                        Generating suggested message…
                      </div>
                    )}
                  </div>
                  <p className="text-white/70 text-sm mt-1">
                    {2000 - consultationMessage.length} characters remaining
                  </p>
                </div>

                <div>
                  <label className="block text-white font-medium mb-3">
                    Preferred time of day <span className="text-red-300">*</span>
                  </label>
                  <div className="flex gap-4 flex-wrap">
                    {['Morning', 'Afternoon', 'Evening'].map((time) => (
                      <label
                        key={time}
                        className="flex items-center gap-2 text-white cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={preferredTimes.includes(time)}
                          onChange={() => toggleTime(time)}
                          className="w-4 h-4 rounded border-white/30 bg-white/10 text-[#3d7872] focus:ring-2 focus:ring-white/50"
                        />
                        <span>{time}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-white font-medium mb-3">
                    Preferred days <span className="text-red-300">*</span>
                  </label>
                  <div className="flex gap-4 flex-wrap">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                      <label
                        key={day}
                        className="flex items-center gap-2 text-white cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={preferredDays.includes(day)}
                          onChange={() => toggleDay(day)}
                          className="w-4 h-4 rounded border-white/30 bg-white/10 text-[#3d7872] focus:ring-2 focus:ring-white/50"
                        />
                        <span>{day}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {submitError && (
                  <p className="text-sm text-red-300 bg-red-900/30 rounded-md px-3 py-2">
                    {submitError}
                  </p>
                )}
                {submitSuccess && (
                  <p className="text-sm text-green-300 bg-green-900/30 rounded-md px-3 py-2">
                    Consultation request sent successfully!
                  </p>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1 bg-transparent border-2 border-white text-white hover:bg-white/10"
                    disabled={isSubmitting}
                    onClick={() => {
                      stopPolling();
                      setConsultationModalOpen(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-[#e8a145] hover:bg-[#d89135] text-white font-medium"
                    disabled={isSubmitting || submitSuccess}
                    onClick={() => void handleSendConsultationRequest()}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={14} className="mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send Request'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
